import { useCallback, useEffect, useRef, useState } from 'react'
import { AddressDetails, CustomerAddress } from 'shared-types'
import { formatAddressfinderAddress } from '~/helpers/formatCustomerAddress'

import {
  PO_BOXES_NUMBER_REGEX,
  PO_BOXES_PREFIX_REGEX,
} from '~/validation-schemas/regex/address.regex'
import { Client } from '../../customClients/client'
import { useGoogleMapsLoader } from '../useGoogleMapsLoader'
import { usePage } from '../usePage'
import { useSession } from '../useSession'

export const useSearchPlace = () => {
  const { countryCode } = usePage()
  const { session } = useSession()
  const [places, setPlaces] = useState<google.maps.places.PlacesService>(null)
  const { isLoaded } = useGoogleMapsLoader()

  const placeServiceMounted = useRef(false)
  useEffect(() => {
    if (isLoaded && !placeServiceMounted.current) {
      placeServiceMounted.current = true
      const googlePlaces = new google.maps.places.PlacesService(
        document.createElement('div')
      )
      setPlaces(googlePlaces)
    }
  }, [isLoaded])

  const searchPlaceAddress = useCallback(
    (query: string) => {
      return new Promise<google.maps.places.PlaceResult[]>((resolve) => {
        places?.findPlaceFromQuery(
          {
            query: `${query} ${countryCode || 'AU'}`,
            fields: ['name', 'geometry', 'place_id'],
          },
          (result, status) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK) {
              resolve([])
              return
            }

            if (!result?.length) {
              resolve([])
              return
            }

            resolve(result)
          }
        )
      })
    },
    [countryCode, places]
  )

  const searchPlaceDetails = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (query: string) => {
      return new Promise<google.maps.places.PlaceResult>((resolve) => {
        places?.findPlaceFromQuery(
          {
            query: `${query} ${countryCode || 'AU'}`,
            fields: ['place_id'],
          },
          (result, status) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK) {
              resolve(null)
              return
            }

            if (!result?.length || !result?.[0]?.place_id) {
              resolve(null)
              return
            }

            places.getDetails(
              {
                placeId: result[0].place_id,
                fields: [
                  'address_components',
                  'geometry',
                  'formatted_address',
                  'place_id',
                ],
              },
              (detail, status) => {
                if (status !== google.maps.places.PlacesServiceStatus.OK) {
                  resolve(null)
                  return
                }

                if (PO_BOXES_PREFIX_REGEX.test(query)) {
                  const poBoxMatch = query.match(PO_BOXES_NUMBER_REGEX)

                  if (poBoxMatch) {
                    const poBoxNumber = poBoxMatch[1]

                    detail?.address_components?.unshift({
                      long_name: `PO Box ${poBoxNumber}`,
                      short_name: `PO Box ${poBoxNumber}`,
                      types: ['subpremise'],
                    })

                    detail.formatted_address = `PO Box ${poBoxNumber}, ${detail.formatted_address}`
                  }
                }
                resolve(detail)
              }
            )
          }
        )
      })
    },
    [countryCode, places]
  )

  const getAddressDetailsByQuery = useCallback(
    (address: string) => {
      return Client.addressfinder.getAddressDetailsByQuery(address, session)
    },
    [session]
  )

  const isMatchedAddress = (
    existingAddress: CustomerAddress,
    data: AddressDetails
  ) => {
    const strAddress = `${existingAddress.country}${existingAddress.state}${existingAddress.postalCode}`
    const strData = `${data.country}${data.state}${data.postalCode}`

    return strAddress.toLowerCase() === strData.toLowerCase()
  }

  const recomposeAddress = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (data: CustomerAddress): Promise<CustomerAddress> => {
      let formData = { ...data }

      const formattedAddress = formatAddressfinderAddress(data)
      const addressDetails = await getAddressDetailsByQuery(formattedAddress)

      if (data.isManual) {
        if (addressDetails && isMatchedAddress(data, addressDetails)) {
          formData = {
            ...data,
            ...addressDetails,
            address: formattedAddress,
            addressLine1: data.addressLine1 || '',
            addressLine2: data.addressLine2 || '',
            postalCode: addressDetails.postalCode || data.postalCode,
            streetName: addressDetails.street,
            streetNumber: '',
            city: addressDetails.city || data.city || data.suburb,
            suburb: addressDetails.city || data.city || data.suburb,
          }
        } else {
          formData = {
            ...formData,
            address: formattedAddress,
            placeId: '',
            latitude: '',
            longitude: '',
            streetName: '',
            streetNumber: '',
            city: data.city || data.suburb,
            suburb: data.city || data.suburb,
          }
        }
      } else if (addressDetails && isMatchedAddress(data, addressDetails)) {
        formData = {
          ...addressDetails,
          ...formData,
          addressLine1: '',
          addressLine2: addressDetails.addressLine2 || '',
          streetName: addressDetails.street,
          city: addressDetails.city || formData.city || formData.suburb,
          suburb: addressDetails.city || formData.city || formData.suburb,
        }
      } else {
        formData = {
          ...formData,
          suburb: formData.city || formData.suburb,
          isManual: true,
        }
      }

      return formData
    },
    [getAddressDetailsByQuery]
  )

  return {
    searchPlaceAddress,
    searchPlaceDetails,
    getAddressDetailsByQuery,
    recomposeAddress,
  }
}
